import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    MenuItem,
    RenderIf,
} from 'react-rainbow-components';
import { Flag, Clock } from 'lucide-react';
import ButtonMenu from 'components/ButtonMenu';
import UserAvatar from 'components/UserAvatar';
import DotsVertical from 'components/icons/dotsVertical';
import Check from 'components/icons/check';
import Trash from 'components/icons/trash';
import Pencil from 'components/icons/pencil';
import ChevronDown from 'components/icons/chevronDown';
import ChevronRight from 'components/icons/chevronRight';
import getDisplayName from 'data/services/profile/getDisplayName';
import getInitials from 'data/services/string/getInitials';
import { EntityGet } from 'data/firestore/types';
import { Todo } from 'data/firestore/agent/todo/types';
import { ItemUser } from 'data/firestore/globals';
import { TodoForm, getDateInputValue } from './TodoCreateForm';
import {
    TodoItemWrapper,
    TodoItemRow,
    StatusGroup,
    StatusButton,
    StatusSpinner,
    StatusLabel,
    VerticalDivider,
    TodoContent,
    TodoTitle,
    TodoTitleRow,
    ChildTodosToggleButton,
    TodoDescription,
    TodoActions,
    TodoFooter,
    FooterItem,
    FooterPriorityButton,
    NestedTodosContainer,
    ConfirmOverlay,
    ConfirmActions,
    ConfirmButton,
    ConfirmMessage,
} from './styled';

interface TodoItemProps {
    todo: EntityGet<Todo>;
    isNested?: boolean;
    childTodos?: EntityGet<Todo>[];
    onStatusToggle: (todoId: string, newStatus: string) => void;
    onCancel: (todoId: string) => void;
    onUpdate: (todoId: string, payload: {
        title?: string;
        description?: string;
        status?: 'pending' | 'in_progress' | 'cancelled' | 'done';
        priority?: 'low' | 'medium' | 'high';
        assignedTo?: any | null;
        dueDate?: string | null;
        metadata?: Record<string, unknown>;
    }) => Promise<void>;
    loadingTodoIds: Set<string>;
    agentId: string;
}

const statusCycle = {
    pending: 'in_progress',
    in_progress: 'done',
    done: 'pending',
    cancelled: 'pending',
};

const statusTooltipMap: Record<string, string> = {
    pending: 'Click to start',
    in_progress: 'Click to complete',
    done: 'Click to reopen',
    cancelled: 'Click to reopen',
};

const statusLabelMap: Record<string, string> = {
    pending: 'To do',
    in_progress: 'In progress',
    done: 'Done',
    cancelled: 'Cancelled',
};

const priorityLabelMap: Record<'low' | 'medium' | 'high', string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
};

const priorityVariantMap: Record<'low' | 'medium' | 'high', 'success' | 'warning' | 'error'> = {
    low: 'success',
    medium: 'warning',
    high: 'error',
};

const priorityCycle: Record<string, 'low' | 'medium' | 'high'> = {
    low: 'medium',
    medium: 'high',
    high: 'low',
};

const formatDueDate = (dueDate?: Date | string | null) => {
    if (!dueDate) return '';
    const parsed = dueDate instanceof Date ? dueDate : new Date(dueDate);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const getRelativeDueLabel = (dueDate?: Date | string | null) => {
    if (!dueDate) return '';
    const parsed = dueDate instanceof Date ? dueDate : new Date(dueDate);
    if (Number.isNaN(parsed.getTime())) return '';

    const now = new Date();
    const diffMs = parsed.getTime() - now.getTime();
    const diffMins = Math.round(diffMs / (1000 * 60));
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins >= 0 && diffMins < 60) return 'Due soon';
    if (diffHours >= 1 && diffHours < 24) return `Due in ${diffHours}h`;
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays > 1 && diffDays <= 7) return `Due in ${diffDays} days`;
    if (diffDays > 7) return formatDueDate(dueDate);
    if (diffDays === 0 && diffMins < 0) return 'Overdue today';
    if (diffDays === -1) return 'Overdue 1 day';
    if (diffDays < -1) return `Overdue ${Math.abs(diffDays)} days`;
    return formatDueDate(dueDate);
};

const TodoItem = ({
    todo,
    isNested = false,
    childTodos = [],
    onStatusToggle,
    onCancel,
    onUpdate,
    loadingTodoIds,
    agentId,
}: TodoItemProps) => {
    const isLoading = loadingTodoIds.has(todo.id);
    const isDone = todo.status === 'done';
    const hasChildren = !isNested && childTodos.length > 0;
    const [isEditing, setIsEditing] = useState(false);
    const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
    const [isChildrenExpanded, setIsChildrenExpanded] = useState(!isDone);
    const [editTitle, setEditTitle] = useState(todo.title);
    const [editDescription, setEditDescription] = useState(todo.description || '');
    const [editAssignedTo, setEditAssignedTo] = useState<ItemUser | null>(todo.assignedTo || null);
    const [editPriority, setEditPriority] = useState<'low' | 'medium' | 'high' | ''>(todo.priority || '');
    const [editDueDate, setEditDueDate] = useState(
        getDateInputValue(todo.dueDate as Date | string | null),
    );
    const dueDateLabel = formatDueDate(todo.dueDate as Date | string | null);
    const relativeDueLabel = useMemo(
        () => getRelativeDueLabel(todo.dueDate as Date | string | null),
        [todo.dueDate],
    );
    const dueDateMs = todo.dueDate ? new Date(todo.dueDate as Date | string).getTime() : NaN;
    const isDue = Boolean(
        dueDateLabel
        && Number.isFinite(dueDateMs)
        && dueDateMs <= Date.now()
        && todo.status !== 'done'
        && todo.status !== 'cancelled',
    );

    useEffect(() => {
        if (isDone && hasChildren) {
            setIsChildrenExpanded(false);
        }
    }, [isDone, hasChildren]);

    const handleStatusToggle = useCallback(() => {
        if (isLoading) return;
        const newStatus = statusCycle[todo.status as keyof typeof statusCycle];
        onStatusToggle(todo.id, newStatus);
    }, [todo.id, todo.status, onStatusToggle, isLoading]);

    const handleCancelRequest = useCallback(() => {
        setIsConfirmingCancel(true);
    }, []);

    const handleConfirmCancel = useCallback(() => {
        if (isLoading) return;
        setIsConfirmingCancel(false);
        onCancel(todo.id);
    }, [todo.id, onCancel, isLoading]);

    const handleDismissCancel = useCallback(() => {
        setIsConfirmingCancel(false);
    }, []);

    const handleToggleChildren = useCallback(() => {
        if (!hasChildren) return;
        setIsChildrenExpanded((prev) => !prev);
    }, [hasChildren]);

    const handleStartEdit = useCallback(() => {
        if (isLoading) return;
        setEditTitle(todo.title);
        setEditDescription(todo.description || '');
        setEditAssignedTo(todo.assignedTo || null);
        setEditPriority(todo.priority || '');
        setEditDueDate(getDateInputValue(todo.dueDate as Date | string | null));
        setIsEditing(true);
    }, [isLoading, todo.title, todo.description, todo.assignedTo, todo.priority, todo.dueDate]);

    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
    }, []);

    const handleCyclePriority = useCallback(() => {
        if (isLoading || isDone) return;
        const currentPriority = todo.priority || 'low';
        const nextPriority = priorityCycle[currentPriority] || 'medium';
        onUpdate(todo.id, { priority: nextPriority });
    }, [todo.id, todo.priority, onUpdate, isLoading, isDone]);

    const assigneeDisplayName = getDisplayName(todo.assignedTo);
    const assigneeInitials = assigneeDisplayName === 'Unknown'
        ? null
        : getInitials(assigneeDisplayName);
    const hasAssignee = Boolean(todo.assignedTo?.uid);

    const handleSaveEdit = useCallback(async () => {
        if (!editTitle.trim() || isLoading) return;
        await onUpdate(todo.id, {
            title: editTitle.trim(),
            description: editDescription.trim(),
            assignedTo: editAssignedTo || null,
            priority: editPriority || undefined,
            dueDate: editDueDate ? new Date(editDueDate).toISOString() : null,
        });
        setIsEditing(false);
    }, [
        todo.id,
        editTitle,
        editDescription,
        editAssignedTo,
        editPriority,
        editDueDate,
        onUpdate,
        isLoading,
    ]);

    const statusTooltip = statusTooltipMap[todo.status] || `Status: ${todo.status}`;
    const hasFooterMeta = Boolean(todo.priority)
        || Boolean(dueDateLabel)
        || (!isDone && hasAssignee);

    const todoRow = isEditing ? (
        <TodoForm
            agentId={agentId}
            title={editTitle}
            description={editDescription}
            assignedTo={editAssignedTo}
            priority={editPriority}
            dueDate={editDueDate}
            isSubmitting={isLoading}
            submitLabel="Save"
            submittingLabel="Saving..."
            onTitleChange={setEditTitle}
            onDescriptionChange={setEditDescription}
            onAssignedToChange={setEditAssignedTo}
            onPriorityChange={setEditPriority}
            onDueDateChange={setEditDueDate}
            onSubmit={handleSaveEdit}
            onCancel={handleCancelEdit}
        />
    ) : (
        <TodoItemWrapper $isNested={isNested} $isCompact={isDone}>
            {isConfirmingCancel && (
                <ConfirmOverlay>
                    <ConfirmMessage>Cancel this TODO?</ConfirmMessage>
                    <ConfirmActions>
                        <ConfirmButton variant="danger" onClick={handleConfirmCancel}>
                            Yes, cancel
                        </ConfirmButton>
                        <ConfirmButton variant="secondary" onClick={handleDismissCancel}>
                            No, keep it
                        </ConfirmButton>
                    </ConfirmActions>
                </ConfirmOverlay>
            )}
            <TodoItemRow $isCompact={isDone}>
                <RenderIf isTrue={hasChildren}>
                    <ChildTodosToggleButton
                        type="button"
                        onClick={handleToggleChildren}
                        aria-expanded={isChildrenExpanded}
                        aria-label={isChildrenExpanded ? 'Collapse child todos' : 'Expand child todos'}
                        title={isChildrenExpanded ? 'Collapse child todos' : 'Expand child todos'}
                    >
                        {isChildrenExpanded ? <ChevronDown /> : <ChevronRight />}
                    </ChildTodosToggleButton>
                </RenderIf>
                <StatusGroup>
                    <StatusButton
                        status={todo.status}
                        onClick={handleStatusToggle}
                        title={statusTooltip}
                        disabled={isLoading}
                        $isCompact={isDone}
                    >
                        <RenderIf isTrue={isLoading}>
                            <StatusSpinner status={todo.status} />
                        </RenderIf>
                        <RenderIf isTrue={!isLoading && todo.status === 'done'}>
                            <Check />
                        </RenderIf>
                    </StatusButton>
                    <StatusLabel status={todo.status}>
                        {statusLabelMap[todo.status] || todo.status}
                    </StatusLabel>
                </StatusGroup>

                <VerticalDivider />

                <TodoContent $isCompact={isDone}>
                    <TodoTitleRow $isCompact={isDone}>
                        <TodoTitle status={todo.status}>
                            {todo.title}
                        </TodoTitle>
                    </TodoTitleRow>
                    <RenderIf isTrue={!isDone && Boolean(todo.description)}>
                        <TodoDescription status={todo.status}>
                            {todo.description}
                        </TodoDescription>
                    </RenderIf>
                </TodoContent>

                <RenderIf isTrue={!isDone}>
                    <TodoActions>
                        <ButtonMenu
                            menuAlignment="right"
                            menuSize="x-small"
                            buttonVariant="base"
                            icon={<DotsVertical />}
                        >
                            <MenuItem
                                label="Edit"
                                onClick={handleStartEdit}
                                icon={<Pencil />}
                            />
                            <MenuItem
                                label="Cancel"
                                onClick={handleCancelRequest}
                                icon={<Trash />}
                            />
                        </ButtonMenu>
                    </TodoActions>
                </RenderIf>
            </TodoItemRow>
            <RenderIf isTrue={hasFooterMeta}>
                <TodoFooter>
                    <RenderIf isTrue={Boolean(todo.priority)}>
                        {isDone ? (
                            <FooterItem $color={priorityVariantMap[todo.priority as 'low' | 'medium' | 'high']}>
                                <Flag size={11} />
                                {priorityLabelMap[todo.priority as 'low' | 'medium' | 'high']}
                            </FooterItem>
                        ) : (
                            <FooterPriorityButton
                                type="button"
                                onClick={handleCyclePriority}
                                title="Click to change priority"
                                $color={priorityVariantMap[todo.priority as 'low' | 'medium' | 'high']}
                            >
                                <Flag size={11} />
                                {priorityLabelMap[todo.priority as 'low' | 'medium' | 'high']}
                            </FooterPriorityButton>
                        )}
                    </RenderIf>
                    <RenderIf isTrue={Boolean(dueDateLabel)}>
                        <FooterItem
                            $color={isDue ? 'error' : 'default'}
                            title={formatDueDate(todo.dueDate as Date | string | null)}
                        >
                            <Clock size={11} />
                            {isDone
                                ? formatDueDate(todo.dueDate as Date | string | null)
                                : relativeDueLabel}
                        </FooterItem>
                    </RenderIf>
                    <RenderIf isTrue={!isDone && hasAssignee}>
                        <FooterItem $color="default">
                            <UserAvatar
                                initials={assigneeInitials}
                                value={todo.assignedTo?.uid}
                                src={todo.assignedTo?.photoURL}
                                size="x-small"
                            />
                            {assigneeDisplayName}
                        </FooterItem>
                    </RenderIf>
                </TodoFooter>
            </RenderIf>
        </TodoItemWrapper>
    );

    if (isNested) {
        return todoRow;
    }

    return (
        <>
            {todoRow}
            <RenderIf isTrue={hasChildren && isChildrenExpanded}>
                <NestedTodosContainer>
                    {childTodos.map((childTodo) => (
                        <TodoItem
                            key={childTodo.id}
                            todo={childTodo}
                            isNested
                            onStatusToggle={onStatusToggle}
                            onCancel={onCancel}
                            onUpdate={onUpdate}
                            loadingTodoIds={loadingTodoIds}
                            agentId={agentId}
                        />
                    ))}
                </NestedTodosContainer>
            </RenderIf>
        </>
    );
};

export default TodoItem;
